import { useActivePublicTeam } from '~/hooks-data/team/use-active-public-team'
import { Role, RoleEnum } from '@basehub/data/replicache/index'
import { useUserRoleStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/role-store'
import {
  hasGuestAccess,
  isGuestInRepo,
} from '@basehub/data/replicache/mutators/team-space/team/auth'
import { useUserStore } from '~/app/_store/user-store'
import {
  useBranchOrCommitSegment,
  useSelectedBranchOrCommitQuery,
} from '~/lib/commits/use-selected-branch-or-commit'
import { useSelectedTeamAndRepo } from './use-selected-team-and-repo'

export const useUserRole = () => {
  return useUserRoleStore((s) => s.userRole)
}

export const useOptionalRole = () => {
  try {
    // eslint-disable-next-line react-compiler/react-compiler
    return useUserRole()
  } catch {
    return null
  }
}

export const useIsViewer = () => {
  return useUserRole() === RoleEnum.viewer
}

export const isMemberOrHigher = (role: Role): role is 'member' | 'owner' => {
  return role === RoleEnum.member || role === RoleEnum.owner
}

export const isEditorOrHigher = (
  role: Role
): role is 'editor' | 'member' | 'owner' => {
  return role === RoleEnum.editor || isMemberOrHigher(role)
}

export const useIsMemberOrHigher = () => {
  const role = useUserRole()
  return isMemberOrHigher(role)
}

export const useIsEditorOrHigher = () => {
  const role = useUserRole()
  return isEditorOrHigher(role)
}

export const useIsExternalVisitor = () => {
  const activePublicTeam = useActivePublicTeam()
  return Boolean(
    activePublicTeam.isInitialLoading || activePublicTeam.data?.team.isPublic
  )
}

export const useIsGuest = () => {
  const repo = useSelectedTeamAndRepo().data?.repo
  const activePublicTeam = useActivePublicTeam()
  const userId = useUserStore((s) => s.optimisticUser?.id)
  if (
    activePublicTeam.isInitialLoading &&
    activePublicTeam.data === undefined
  ) {
    return undefined
  }

  if (!activePublicTeam.data?.repo || !userId) return false

  return isGuestInRepo({
    repo: repo ?? activePublicTeam.data.repo,
    userId,
    allowVisitorInPublicRepos: false,
  })
}

export const useIsGuestInHisBranch = () => {
  const branchOrCommit = useBranchOrCommitSegment().branchOrCommit
  const repo = useSelectedTeamAndRepo().data?.repo
  const userId = useUserStore((s) => s.optimisticUser?.id)
  if (!branchOrCommit || !userId || !repo) return false

  return hasGuestAccess({
    branch: branchOrCommit,
    isGuestInHisBranch: undefined,
    repo,
    userId,
    guestAccess: 'branch-author',
  })
}

export const useIsGuestInOthersBranch = () => {
  const branchOrCommit = useSelectedBranchOrCommitQuery().data

  const teamAndRepo = useSelectedTeamAndRepo().data
  const team = teamAndRepo?.team
  const repo = teamAndRepo?.repo

  const userId = useUserStore((s) => s.optimisticUser?.id)
  if (!branchOrCommit || !userId || !repo) return true
  // is member, is not guest
  if (team?.members.some((member) => member.userId === userId)) return false

  return !hasGuestAccess({
    branch: branchOrCommit,
    isGuestInHisBranch: undefined,
    repo,
    userId,
    guestAccess: 'branch-author',
    playgroundId: branchOrCommit?.playgroundId,
  })
}
